import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { Container, Item } from 'Components/Grid';
import HistoryRowControls from 'Components/HistoryRowControls';
import HistoryRowListing from 'Components/HistoryRowListing';
import HistorySection from 'Components/HistorySection';
import DocumentCreationButton from 'Components/DocumentCreationButton';
import { statementStyle } from '../Components/Statement';
import { EditingLockedContentContext } from 'Containers/EditingLockedContentContext';
import { exists } from 'neuro-utils';

const TitleValue = (title: React.JSX.Element, value: string | React.JSX.Element): React.JSX.Element => (
  <Container>
    <Item xs={10}>{title}</Item>
    <Item xs={2}>{value}</Item>
  </Container>
);

const showProcedureCode = (d: IDocumentWithTypeV2<'scoliosis'>) => d.procedureCode && d.procedureCode.length > 0;
const showStatement = (d: IDocumentWithTypeV2<'scoliosis'>) => d.statement && d.statement.length > 0;

const Scoliosis = ({ documents, startEdit }: IOwnProps): React.JSX.Element => {
  const showProcedureCodeHeader = documents.some(d => showProcedureCode(d));
  const showStatementHeader = documents.some(d => showStatement(d));

  const { openEditing } = React.useContext(EditingLockedContentContext);

  return (
    <HistorySection
      headerText={<FormattedMessage id={'imaging.scoliosis'} />}
      newButton={
        <DocumentCreationButton
          name="scoliosis"
          text={'imaging.newScoliosisRontgen'}
          onClick={startEdit({}, 'scoliosis')}
          width={15}
          height={3}
          fontSize={12}
          alternate={true}
        />
      }
    >
      <HistoryRowListing
        documents={documents}
        headers={
          <>
            {showProcedureCodeHeader && (
              <Item xs={2}>
                <FormattedMessage id="imaging.procedureCode" />
              </Item>
            )}
            {showStatementHeader && (
              <Item xs={3}>
                <FormattedMessage id="imaging.statementText" />
              </Item>
            )}
            <Item xs={4}>
              <FormattedMessage id="imaging.coronalProfile" />
            </Item>
            <Item xs={3}>
              <FormattedMessage id="imaging.sagittalProfile" />
            </Item>
          </>
        }
        contentFormat={d => (
          <>
            {showProcedureCodeHeader && <Item xs={2}>{showProcedureCode(d) ? d.procedureCode : ''}</Item>}
            {showStatementHeader && (
              <Item xs={3} style={statementStyle}>
                {showStatement(d) ? d.statement : ''}
              </Item>
            )}
            <Item xs={4} id={'proximalThoracic'}>
              {TitleValue(
                <FormattedMessage id="imaging.proximalThoracicCobb" />,
                exists(d.proximalThoracicCobb) ? `${d.proximalThoracicCobb}°` : '-',
              )}
              {TitleValue(
                <FormattedMessage id="imaging.thoracicCobb" />,
                exists(d.thoracicCobb) ? `${d.thoracicCobb}°` : '-',
              )}
              {TitleValue(
                <FormattedMessage id="imaging.thoracolumbarCobb" />,
                exists(d.thoracolumbarCobb) ? `${d.thoracolumbarCobb}°` : '-',
              )}
            </Item>
            <Item xs={3}>
              {TitleValue(
                <FormattedMessage id="imaging.kyphosist2t12" />,
                exists(d.kyphosist2t12) ? `${d.kyphosist2t12}°` : '-',
              )}
              {TitleValue(
                <FormattedMessage id="imaging.kyphosist5t12" />,
                exists(d.kyphosist5t12) ? `${d.kyphosist5t12}°` : '-',
              )}
              {TitleValue(
                <FormattedMessage id="imaging.lordosist12s1" />,
                exists(d.lordosist12s1) ? `${d.lordosist12s1}°` : '-',
              )}
            </Item>
          </>
        )}
        makeDate
        makeControls
        historyRowControls={d => (
          <HistoryRowControls
            document={d}
            startEdit={
              d._lockedFor && d._source !== null ? doc => () => openEditing(doc, 'imaging.scoliosis') : startEdit
            }
          />
        )}
      />
    </HistorySection>
  );
};

interface IOwnProps {
  documents: IDocumentWithTypeV2<'scoliosis'>[];
  startEdit: StartEdit;
}

export default Scoliosis;
